import type {
  ModelAssetNode,
  ProjectModelLoadResult,
} from '@puppycad/cad-core';
import {
  decodeAssemblyChildNodeReference,
  type AssemblyChildNodeSummary,
} from '@/services/assembly-branch-node-list';

/**
 * 左侧装配分支节点列表的 hover 定位引用。
 *
 * 设计说明：
 * 1. 左侧节点 ID 是 service 边界编码出的业务引用，不是 Three.js 对象 ID；
 * 2. hover 链路必须先还原业务引用，再由项目模型加载结果定位场景对象；
 * 3. 该类型只保留定位一级子节点所需字段，避免 UI 层理解更多后端协议细节。
 */
export interface AssemblyBranchNodeHoverReference {
  /** 子节点所属仓库 ID。 */
  readonly repositoryId: string;
  /** 父装配中该子节点实例 UUID。 */
  readonly instanceUuid?: string;
  /** 父装配当前采纳的子节点提交 ID。 */
  readonly adoptedCommitId?: string | null;
  /** 父装配 workAssemble 中的稳定结构 key。 */
  readonly nodeKey?: string;
}

/**
 * 左侧装配分支节点 hover 目标解析输入。
 */
export interface ResolveAssemblyBranchNodeHoverTargetInput {
  /** 当前被 hover 的左侧节点摘要。 */
  readonly node: AssemblyChildNodeSummary;
  /** 当前项目模型加载结果。 */
  readonly loadResult: ProjectModelLoadResult | null;
  /** 按业务引用定位当前项目模型一级子节点。 */
  readonly resolveFirstLevelNodeByReference:
    (reference: AssemblyBranchNodeHoverReference) => ModelAssetNode | null;
  /** root `.puppy` 重新加载后，通过持久化装配节点 ID 定位离线包内部 hover 目标。 */
  readonly resolveRuntimeHoverTargetByReference?:
    (reference: AssemblyBranchNodeHoverReference) => AssemblyBranchNodeHoverTarget | null;
}

/**
 * 左侧装配分支节点 hover 在场景中的动态高亮目标。
 */
export interface AssemblyBranchNodeHoverTarget {
  /** 项目模型节点 key，用于调试和后续扩展。 */
  readonly nodeKey: string;
  /** 设计树或选择中心可消费的场景对象 ID。 */
  readonly objectId: string;
  /** 目标节点类型，用于传递 SolidWorks 装配/零件 hover 语义。 */
  readonly nodeType: ModelAssetNode['nodeType'];
}

/**
 * 读取项目模型节点对应的首个场景对象 ID。
 *
 * 设计说明：
 * 1. 项目模型组合器会先注册节点容器，再注册节点包根对象；
 * 2. 右侧设计树当前也消费首个 objectId 作为节点 hover/选择锚点；
 * 3. 左侧节点列表沿用同一锚点，避免在同一节点上叠出多个嵌套包围盒。
 *
 * @param loadResult 当前项目模型加载结果。
 * @param nodeKey 项目模型节点 key。
 * @returns 命中的首个场景对象 ID；没有几何或绑定失效时返回 null。
 */
function resolvePrimarySceneObjectId(
  loadResult: ProjectModelLoadResult,
  nodeKey: string,
): string | null {
  for (const binding of loadResult.sceneBindings) {
    if (binding.nodeKey !== nodeKey) {
      continue;
    }

    const objectId = binding.objectId.trim();
    if (objectId) {
      return objectId;
    }
  }

  return null;
}

/**
 * 将左侧装配分支节点摘要解析为场景动态高亮目标。
 *
 * 设计说明：
 * 1. hover 属于 SolidWorks Dynamic Highlight，不应因为旧数据或空节点抛错中断页面；
 * 2. 旧节点 ID、尚未加载几何的空节点、场景绑定缺失都静默返回 null；
 * 3. 真正的版本切换、加载和模型状态错误仍由对应业务入口显式报错。
 *
 * @param input hover 目标解析输入。
 * @returns 可传给 Viewer 程序化 hover 的目标；无法定位时返回 null。
 */
export function resolveAssemblyBranchNodeHoverTarget(
  input: ResolveAssemblyBranchNodeHoverTargetInput,
): AssemblyBranchNodeHoverTarget | null {
  if (!input.loadResult) {
    return null;
  }

  let reference: AssemblyBranchNodeHoverReference;
  try {
    const decodedReference = decodeAssemblyChildNodeReference(input.node.nodeId);
    reference = {
      repositoryId: decodedReference.repositoryId,
      instanceUuid: decodedReference.instanceUuid,
      adoptedCommitId: decodedReference.adoptedCommitId,
      ...(decodedReference.nodeKey ? { nodeKey: decodedReference.nodeKey } : {}),
    };
  } catch {
    return null;
  }

  const modelNode = input.resolveFirstLevelNodeByReference(reference);
  if (!modelNode) {
    return input.resolveRuntimeHoverTargetByReference?.(reference) ?? null;
  }

  const objectId = resolvePrimarySceneObjectId(input.loadResult, modelNode.nodeKey);
  if (!objectId) {
    return input.resolveRuntimeHoverTargetByReference?.(reference) ?? null;
  }

  return {
    nodeKey: modelNode.nodeKey,
    objectId,
    nodeType: modelNode.nodeType,
  };
}
